"use client";

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
} from "react";

// =====================================================
// Daten: DDR (sichtbarer Section-Inhalt) + Heute (Reveal-Inhalt)
// =====================================================

export interface StoryLine {
  text: string;
  // Wenn gesetzt: dieses Wort/Phrase am Ende der Zeile wird in Highlight-Farbe gefärbt
  highlight?: string;
}

export const ddrLines: StoryLine[] = [
  { text: "Wer Land besaß, verlor es.", highlight: "verlor es." },
  { text: "Wer Vieh hatte, verlor es." },
  { text: "Wer sich wehrte, verschwand.", highlight: "verschwand." },
  { text: "" },
  { text: "Wer arbeitete, arbeitete für den Plan." },
  { text: "Wer produzierte, lieferte ab." },
  { text: "Wer Bauer war, kam in die LPG.", highlight: "in die LPG." },
  { text: "Wer sich weigerte, kam ins Lager.", highlight: "ins Lager." },
  { text: "" },
  { text: "Wer Bargeld hatte, gab es ab." },
  { text: "Wer Vermögen hatte, wurde enteignet.", highlight: "enteignet." },
  { text: "Wer schweigen wollte, wurde abgehört.", highlight: "abgehört." },
  { text: "" },
  { text: "Eine ganze Schicht wurde still und legal enteignet.", highlight: "enteignet." },
];

export const todayLines: StoryLine[] = [
  { text: "Wer eine Heizung hat, muss sie tauschen." },
  { text: "Wer das Haus erbt, zahlt drauf.", highlight: "zahlt drauf." },
  { text: "Wer nicht zahlen kann, verkauft.", highlight: "verkauft." },
  { text: "" },
  { text: "Wer arbeitet, verliert real.", highlight: "verliert real." },
  { text: "Wer spart, verliert mehr." },
  { text: "Wer erbt, zahlt doppelt." },
  { text: "Wer hat, hat es nicht lange.", highlight: "nicht lange." },
  { text: "" },
  { text: "Wer mit Bargeld zahlt, wird gemeldet." },
  { text: "Wer Vermögen hat, wird gelistet.", highlight: "gelistet." },
  { text: "Wer schweigt, wird gefunden.", highlight: "gefunden." },
  { text: "" },
  { text: "Eine ganze Mittelschicht wird still und legal enteignet.", highlight: "enteignet." },
];

// =====================================================
// Farben + Typografie
// =====================================================

export const historyFont =
  '"Avant Garde", "Century Gothic", "Futura", -apple-system, BlinkMacSystemFont, sans-serif';
const textBeige = "#b7ab98";
const textDim = "#3d3a36";
const bgColor = "#0d0d0d";
const highlightRed = "#E85D3B";
const highlightRedDim = "#4a2f28";

// Heute-Layer: dunkler Text auf orangem Reveal-Hintergrund
const todayTextColor = "#0d0d0d";
const todayHighlight = "#FFFFFF";

// =====================================================
// Helpers
// =====================================================

function lerpColor(from: string, to: string, t: number): string {
  const f = parseInt(from.slice(1), 16);
  const tC = parseInt(to.slice(1), 16);
  const r = Math.round(((f >> 16) & 0xff) + (((tC >> 16) & 0xff) - ((f >> 16) & 0xff)) * t);
  const g = Math.round(((f >> 8) & 0xff) + (((tC >> 8) & 0xff) - ((f >> 8) & 0xff)) * t);
  const b = Math.round((f & 0xff) + ((tC & 0xff) - (f & 0xff)) * t);
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}

// =====================================================
// ScrollLine: Eine Zeile mit Scroll-Wipe-Effekt + optionalem Highlight
// =====================================================

interface ScrollLineProps {
  line: StoryLine;
  fillPercentage: number;
  fadeOpacity?: number;
  textColor: string;
  textDimColor: string;
  highlightColor: string;
  highlightDimColor: string;
  bgFadeColor: string; // wohin der fade-out interpoliert (BG-Farbe)
}

function ScrollLine({
  line,
  fillPercentage,
  fadeOpacity = 1,
  textColor,
  textDimColor,
  highlightColor,
  highlightDimColor,
  bgFadeColor,
}: ScrollLineProps) {
  if (!line.text) {
    return <div style={{ height: "2rem" }} />;
  }

  const fadeFactor = 1 - fadeOpacity;
  const fadedText = lerpColor(textColor, bgFadeColor, fadeFactor);
  const fadedDim = lerpColor(textDimColor, bgFadeColor, fadeFactor);

  const textStyle: React.CSSProperties = {
    fontFamily: historyFont,
    fontWeight: 700,
    fontSize: "clamp(1.15rem, 2.8vw, 2.5rem)",
    letterSpacing: "-0.02em",
    lineHeight: "140%",
  };

  if (line.highlight) {
    const idx = line.text.lastIndexOf(line.highlight);
    const beforeHighlight = idx >= 0 ? line.text.slice(0, idx) : line.text;
    const highlightText = idx >= 0 ? line.text.slice(idx) : "";

    const totalLength = line.text.length;
    const beforeLength = beforeHighlight.length;
    const highlightStart = totalLength > 0 ? (beforeLength / totalLength) * 100 : 0;

    const beforeFill =
      fillPercentage <= highlightStart
        ? highlightStart > 0
          ? (fillPercentage / highlightStart) * 100
          : 100
        : 100;

    const highlightFill =
      fillPercentage <= highlightStart
        ? 0
        : Math.min(
            100,
            ((fillPercentage - highlightStart) / Math.max(1, 100 - highlightStart)) * 100
          );

    return (
      <div className="mask-collision-target" style={textStyle}>
        <span
          style={{
            backgroundImage: `linear-gradient(to right, ${fadedText} ${beforeFill}%, ${fadedDim} ${beforeFill}%)`,
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
          }}
        >
          {beforeHighlight}
        </span>
        <span
          style={{
            backgroundImage: `linear-gradient(to right, ${highlightColor} ${highlightFill}%, ${highlightDimColor} ${highlightFill}%)`,
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
          }}
        >
          {highlightText}
        </span>
      </div>
    );
  }

  return (
    <div
      className="mask-collision-target"
      style={{
        ...textStyle,
        backgroundImage: `linear-gradient(to right, ${fadedText} ${fillPercentage}%, ${fadedDim} ${fillPercentage}%)`,
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        color: "transparent",
      }}
    >
      {line.text}
    </div>
  );
}

// =====================================================
// StoryContent: Komplette Section-UI (Label + Year-Marker + Lines + Bottom Accent)
// =====================================================

interface StoryContentProps {
  topLabel: string;
  yearLabel: string;
  lines: StoryLine[];
  lineProgress: number[];
  lineFadeProgress: number[];
  fadeOutProgress: number;
  textColor: string;
  textDimColor: string;
  highlightColor: string;
  highlightDimColor: string;
  bgFadeColor: string;
  staticReveal?: boolean;
}

export function StoryContent({
  topLabel,
  yearLabel,
  lines,
  lineProgress,
  lineFadeProgress,
  fadeOutProgress,
  textColor,
  textDimColor,
  highlightColor,
  highlightDimColor,
  bgFadeColor,
  staticReveal = false,
}: StoryContentProps) {
  const normalOpacity = 1 - fadeOutProgress;
  const fadedLabelColor = lerpColor(textColor, bgFadeColor, fadeOutProgress);

  // Border-Farben werden aus dem textColor interpoliert (für Light/Dark Mode-Harmonie)
  const labelBorderRGB = textColor === "#0d0d0d" ? "13, 13, 13" : "183, 171, 152";
  const fadedBorderColor = `rgba(${labelBorderRGB}, ${0.15 * normalOpacity})`;
  const fadedAccentBorderColor = `rgba(${labelBorderRGB}, ${0.3 * normalOpacity})`;

  return (
    <div
      style={{
        position: "relative",
        zIndex: 10,
        width: "100%",
        maxWidth: "1536px",
        margin: "0 auto",
        padding: "16px 32px clamp(64px, 8vw, 96px) 32px",
      }}
    >
      <p
        className="font-bold text-sm uppercase tracking-[0.2em] select-none"
        style={{
          marginTop: 0,
          marginBottom: "clamp(24px, 4vw, 48px)",
          color: fadedLabelColor,
        }}
      >
        {topLabel}
      </p>

      <div
        style={{
          textAlign: "center",
          marginBottom: "64px",
          fontFamily: historyFont,
        }}
      >
        <span
          style={{
            display: "inline-block",
            padding: "12px 32px",
            fontSize: "clamp(1rem, 2vw, 1.25rem)",
            letterSpacing: "0.3em",
            textTransform: "uppercase",
            fontWeight: 500,
            color: fadedLabelColor,
            borderTop: `1px solid ${fadedBorderColor}`,
            borderBottom: `1px solid ${fadedBorderColor}`,
          }}
        >
          {yearLabel}
        </span>
      </div>

      <div style={{ textAlign: "left" }}>
        {lines.map((line, index) => (
          <ScrollLine
            key={index}
            line={line}
            fillPercentage={staticReveal ? 100 : lineProgress[index] ?? 0}
            fadeOpacity={staticReveal ? normalOpacity : 1 - (lineFadeProgress[index] ?? 0)}
            textColor={textColor}
            textDimColor={textDimColor}
            highlightColor={highlightColor}
            highlightDimColor={highlightDimColor}
            bgFadeColor={bgFadeColor}
          />
        ))}
      </div>

      <div
        style={{
          marginTop: "64px",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: "64px",
            height: "1px",
            backgroundColor: fadedAccentBorderColor,
          }}
        />
      </div>
    </div>
  );
}

// =====================================================
// HistoryRevealContent: Heute-Story (wird vom LandingMaskOverlay als Reveal-Inhalt gerendert)
// =====================================================

export interface HistoryRevealContentRef {
  // For now keep simple
}

export function HistoryRevealContent({
  fadeOutProgress,
}: {
  fadeOutProgress: number;
}) {
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        backgroundColor: "#F7931A",
        paddingTop: "clamp(64px, 8vw, 96px)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
      }}
    >
      <StoryContent
        topLabel="Die Realität"
        yearLabel="Deutschland, heute"
        lines={todayLines}
        lineProgress={[]}
        lineFadeProgress={[]}
        fadeOutProgress={fadeOutProgress}
        textColor={todayTextColor}
        textDimColor={todayTextColor}
        highlightColor={todayHighlight}
        highlightDimColor={todayHighlight}
        bgFadeColor="#F7931A"
        staticReveal
      />
    </div>
  );
}

// =====================================================
// HistoryStorySection: Nur DDR-Inhalt mit ScrollReveal (kein Layer-System mehr)
// Heute-Reveal wird vom LandingMaskOverlay übernommen
// =====================================================

export const HistoryStorySection = forwardRef<HTMLElement>((_, ref) => {
  const sectionRef = useRef<HTMLElement>(null);
  useImperativeHandle(ref, () => sectionRef.current!, []);

  const [lineProgress, setLineProgress] = useState<number[]>(
    new Array(ddrLines.length).fill(0)
  );
  const [fadeOutProgress, setFadeOutProgress] = useState(0);
  const [lineFadeProgress, setLineFadeProgress] = useState<number[]>(
    new Array(ddrLines.length).fill(0)
  );

  const updateProgress = useCallback(() => {
    if (!sectionRef.current) return;

    const section = sectionRef.current;
    const rect = section.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    const isMobile = windowWidth < 768;
    const isTablet = windowWidth >= 768 && windowWidth <= 1400;

    let startMultiplier: number;
    let endMultiplier: number;

    if (isMobile) {
      startMultiplier = 0.85;
      endMultiplier = -0.1;
    } else if (isTablet) {
      startMultiplier = 0.6;
      endMultiplier = -0.05;
    } else {
      startMultiplier = 0.55;
      endMultiplier = -0.05;
    }

    const startTrigger = windowHeight * startMultiplier;
    const endTrigger = windowHeight * endMultiplier;

    const scrollProgress = Math.max(
      0,
      Math.min(1, (startTrigger - rect.top) / (startTrigger - endTrigger))
    );

    let fadeStartMultiplier: number;
    let fadeEndMultiplier: number;

    if (isMobile) {
      fadeStartMultiplier = -0.25;
      fadeEndMultiplier = -0.75;
    } else if (isTablet) {
      fadeStartMultiplier = -0.15;
      fadeEndMultiplier = -0.5;
    } else {
      fadeStartMultiplier = -0.1;
      fadeEndMultiplier = -0.4;
    }

    const fadeStartTrigger = windowHeight * fadeStartMultiplier;
    const fadeEndTrigger = windowHeight * fadeEndMultiplier;

    const fadeProgress = Math.max(
      0,
      Math.min(1, (fadeStartTrigger - rect.top) / (fadeStartTrigger - fadeEndTrigger))
    );
    setFadeOutProgress(fadeProgress);

    const totalLines = ddrLines.filter((line) => line.text !== "").length;
    const progressPerLine = 1 / totalLines;

    const newProgress = ddrLines.map((line, index) => {
      if (!line.text) return 0;
      const nonEmptyIndex = ddrLines
        .slice(0, index)
        .filter((l) => l.text !== "").length;
      const lineStart = nonEmptyIndex * progressPerLine;
      const lineEnd = (nonEmptyIndex + 1) * progressPerLine;
      if (scrollProgress <= lineStart) return 0;
      if (scrollProgress >= lineEnd) return 100;
      return ((scrollProgress - lineStart) / progressPerLine) * 100;
    });
    setLineProgress(newProgress);

    const fadePerLine = 1 / totalLines;
    const newLineFade = ddrLines.map((line, index) => {
      if (!line.text) return 0;
      const nonEmptyIndex = ddrLines
        .slice(0, index)
        .filter((l) => l.text !== "").length;
      const lineFadeStart = nonEmptyIndex * fadePerLine;
      const lineFadeEnd = (nonEmptyIndex + 1) * fadePerLine;
      if (fadeProgress <= lineFadeStart) return 0;
      if (fadeProgress >= lineFadeEnd) return 1;
      return (fadeProgress - lineFadeStart) / fadePerLine;
    });
    setLineFadeProgress(newLineFade);
  }, []);

  useEffect(() => {
    updateProgress();

    const isMobileDevice = window.innerWidth < 768;

    let rafId: number;
    if (isMobileDevice) {
      const rafLoop = () => {
        updateProgress();
        rafId = requestAnimationFrame(rafLoop);
      };
      rafId = requestAnimationFrame(rafLoop);
    } else {
      const lenis = (window as { __lenis?: { on: (e: string, cb: () => void) => void; off: (e: string, cb: () => void) => void } }).__lenis;
      if (lenis) {
        lenis.on("scroll", updateProgress);
      } else {
        window.addEventListener("scroll", updateProgress, { passive: true });
      }
    }

    window.addEventListener("resize", updateProgress, { passive: true });

    return () => {
      if (isMobileDevice) {
        cancelAnimationFrame(rafId);
      } else {
        const lenis = (window as { __lenis?: { on: (e: string, cb: () => void) => void; off: (e: string, cb: () => void) => void } }).__lenis;
        if (lenis) {
          lenis.off("scroll", updateProgress);
        } else {
          window.removeEventListener("scroll", updateProgress);
        }
      }
      window.removeEventListener("resize", updateProgress);
    };
  }, [updateProgress]);

  return (
    <section
      ref={sectionRef}
      data-mask-reveal-section="history"
      style={{
        position: "relative",
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "clamp(64px, 8vw, 96px)",
        backgroundColor: bgColor,
      }}
    >
      <StoryContent
        topLabel="Unsere Geschichte"
        yearLabel="SBZ / DDR, 1945-1989"
        lines={ddrLines}
        lineProgress={lineProgress}
        lineFadeProgress={lineFadeProgress}
        fadeOutProgress={fadeOutProgress}
        textColor={textBeige}
        textDimColor={textDim}
        highlightColor={highlightRed}
        highlightDimColor={highlightRedDim}
        bgFadeColor={bgColor}
      />
    </section>
  );
});

HistoryStorySection.displayName = "HistoryStorySection";
